import {useEffect, useMemo, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {constructOptions, VisibilityOptions} from 'settings/visibility-options/VisibilityOptions';
import * as vo from 'settings/visibility-options/VisibilityOptions';
import EntityStore from 'stores/EntityStore';

export interface DisplayOptionsState {
  displayOptions: VisibilityOptions | undefined;
  showFormulas: boolean;
  highlightCustomColumns: boolean;
  readOnlyMode: boolean;
}

export default function useDisplayOptionsState(useDisplayOptions: boolean): DisplayOptionsState {
  const displayOptions = useMemo(() => {
    const config = [
      {base: vo.ELN_SHOW_FORMULAS, visible: false, editable: true, sync: true},
      {base: vo.ELN_HIGHLIGHT_CUSTOM_COLUMNS, visible: false, editable: true, sync: true},
    ];

    if (BenchlingConfig.ELN_ENABLE_READ_ONLY_MODE || EntityStore.currentUserHasBenchlingEmail()) {
      config.push({
        base: vo.ELN_READ_ONLY_MODE,
        visible: false,
        editable: true,
        sync: true,
      });
    }

    return constructOptions('note', config);
  }, []);

  const [showFormulas, setShowFormulas] = useState(false);
  const [highlightCustomColumns, setHighlightCustomColumns] = useState(false);
  const [readOnlyMode, setReadOnlyMode] = useState(false);

  useEffect(() => {
    const updateState = (): void => {
      setShowFormulas(displayOptions.getOption(vo.ELN_SHOW_FORMULAS.name)?.get('visible') ?? false);
      setHighlightCustomColumns(
        displayOptions.getOption(vo.ELN_HIGHLIGHT_CUSTOM_COLUMNS.name)?.get('visible') ?? false
      );
      setReadOnlyMode(displayOptions.getOption(vo.ELN_READ_ONLY_MODE.name)?.get('visible') ?? false);
    };

    displayOptions.on('redraw', updateState);
    updateState();

    return () => displayOptions.off('redraw', updateState);
  }, [displayOptions]);

  if (!useDisplayOptions) {
    return {
      displayOptions: undefined,
      showFormulas: false,
      highlightCustomColumns: false,
      readOnlyMode: false,
    };
  }

  return {displayOptions, showFormulas, highlightCustomColumns, readOnlyMode};
}
